import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { hide as hideIntercom } from '@intercom/messenger-js-sdk';
import classnames from 'classnames';
import { TooltipProvider } from '@foodamigos/ui';
import PlatformAgentRoot from './PlatformAgentRoot';
import PlatformAgentButton from './PlatformAgentButton';
import PlatformAgentIntercomLauncher from './PlatformAgentIntercomLauncher';
import usePlatformAgentState from '../../hooks/context/usePlatformAgentState';
import useIntercomVisible from '../../hooks/utility/useIntercomVisible';
import { PLATFORM_AGENT_PANEL_ID, PLATFORM_AGENT_PANEL_LAYOUT } from '../../services/platform-agent/platformAgent';

// Fetched on the first open, never with the page (PRD AC 63–64).
const PlatformAgentPanel = React.lazy(() => import('./PlatformAgentPanel'));

// The row's distance from the window's bottom, and from a bar fixed to the page's bottom (design: 20 px / 12 px).
const ROW_BOTTOM = 20;
const ROW_ABOVE_BAR = 12;

// On <html> while the panel is open: from 1280 px, where the panel sits beside the page, the page's antd dialogs
// and drawers keep to the page and leave the panel visible (index.css, QA29).
export const PLATFORM_AGENT_OPEN_ATTRIBUTE = 'data-platform-agent-open';

// The panel's width beside the page. Tailwind needs literal classes, so the panel's sm:w-[400px], the row's
// sm:right-[420px], Layout's xl:mr-[400px] and index.css's dialog offset spell it out; a test holds them to this.
export const PLATFORM_AGENT_PANEL_WIDTH = 400;

/**
 * The platform agent (Amigo) on every dashboard page: the bottom-right row, 20 px from the corner, and the panel.
 * Nothing at all unless shop's context says the person gets Amigo here, so the page is the same as without it.
 * The panel is 400 px: beside the page from 1280 px (Layout narrows the page), over it with a shadow from 640 px,
 * full screen under 640 px (PLATFORM_AGENT_PANEL_LAYOUT); from 1280 px the page's dialogs open beside it too. The
 * button hides while it is open and takes the focus back when it closes.
 */
export default function PlatformAgent() {
  const { available, launcher } = usePlatformAgentState();
  const open = useSelector((state) => state.platformAgent.open);
  const bottomBar = useSelector((state) => state.platformAgent.bottomBar);
  const intercomVisible = useIntercomVisible();

  const buttonRef = useRef(null);
  const wasOpen = useRef(open);

  useEffect(() => {
    if (wasOpen.current && !open) {
      buttonRef.current?.focus();
    }

    // Opening Amigo closes Intercom's Messenger; without Intercom on the page the SDK does nothing.
    if (open && !wasOpen.current && intercomVisible) {
      hideIntercom();
    }

    wasOpen.current = open;
  }, [open, intercomVisible]);

  // A person following Amigo's steps opens the page's dialogs (Add promo code) while the panel shows the steps.
  const panelOpen = available && open;

  useEffect(() => {
    if (!panelOpen) {
      return undefined;
    }

    document.documentElement.setAttribute(PLATFORM_AGENT_OPEN_ATTRIBUTE, '');

    return () => document.documentElement.removeAttribute(PLATFORM_AGENT_OPEN_ATTRIBUTE);
  }, [panelOpen]);

  const rowStyle = useMemo(() => ({ bottom: bottomBar > 0 ? bottomBar + ROW_ABOVE_BAR : ROW_BOTTOM }), [bottomBar]);

  // Between restaurants (`launcher` without `available`) only Intercom's companion stays, until shop answers.
  if (!available && !(launcher && intercomVisible)) {
    return null;
  }

  return (
    <>
      {/* The row stays while the panel is open, so Intercom keeps its companion launcher: left of the panel from
          640 px, hidden on a phone (the panel is full screen there). */}
      {(!open || intercomVisible) && (
        <PlatformAgentRoot
          className={classnames('fixed right-5 z-50 flex items-center gap-3', {
            'max-sm:hidden sm:right-[420px]': open,
          })}
          style={rowStyle}
        >
          {available && !open && (
            <TooltipProvider>
              <PlatformAgentButton ref={buttonRef} aria-expanded={false} aria-controls={PLATFORM_AGENT_PANEL_ID} />
            </TooltipProvider>
          )}
          {intercomVisible && <PlatformAgentIntercomLauncher />}
        </PlatformAgentRoot>
      )}
      {available && open && (
        <PlatformAgentRoot className={PLATFORM_AGENT_PANEL_LAYOUT}>
          <Suspense fallback={null}>
            <PlatformAgentPanel />
          </Suspense>
        </PlatformAgentRoot>
      )}
    </>
  );
}
